import {HTMLProps} from "react";
import {DrupalProductNode} from "../../types/hygiena-types";
import {DrupalTaxonomyTerm} from "next-drupal";
import SectionHeading from "../organisms/section-heading";
import classNames from "classnames";

interface ProductGroupingTitle extends HTMLProps<HTMLDivElement> {
  node: DrupalProductNode | DrupalProductNode[] | undefined,
  defaultTitle?: string,
  className?: string,
  marginBottom?: string,
  headerType?: number,
  smallTitle?: boolean,
}

/**
 * This returns a product grouping section title based upon the Product Class.
 *
 * @param node
 *   Can take one or multiple nodes, if there is one it will try to derive the title from
 *   its Product Class, otherwise it will derive it from the first product class in the list.
 * @param defaultTitle
 *   The default title to use.
 * @param className
 *   Optional class name.
 * @param headerType
 *   The type of header.
 * @param marginBottom
 *   Optional margin bottom to add.
 * @param smallTitle
 *   Will use 24px (2xl) if true.
 * @param props
 */
export function ProductGroupingTitle(
  {
    node,
    defaultTitle,
    className,
    headerType,
    marginBottom,
    smallTitle,
    ...props
  } : ProductGroupingTitle
): JSX.Element {
  let productClass: DrupalTaxonomyTerm|null = null;
  let headerText: string;
  let link = {} as {uri: string, title: string};

  // Get the Product Class.
  if (Array.isArray(node)) {
    for (const product of node) {
      if (product?.field_product_class) {
        productClass = product?.field_product_class;
        break;
      }
    }
  }
  else if (node?.field_product_class) {
    productClass = node.field_product_class;
  }

  if (!productClass) {
    if (defaultTitle) return <SectionHeading title={defaultTitle} className={className} marginBottom={marginBottom} headingClassName={classNames({
      "max-md:!text-2xl md:!text-4xl" : headerType === 3 && !smallTitle,
      "text-2xl" : smallTitle,
    })} headerType={headerType ?? 2} {...props}/>
    return <></>
  }

  // Derive the link.
  // If there is a Product Listing Link field; use it.
  if (productClass?.field_class_pp_grouping_link) {
    link = productClass.field_class_pp_grouping_link;
  }
  // Alternatively; if Link To Class is checked and Link Text is added, use that.
  else if (productClass?.field_class_link_to_class && productClass?.path?.alias && productClass?.field_class_pp_link_text) {
    link = {
      uri: productClass.path.alias,
      title: productClass.field_class_pp_link_text,
    }
  }
  // Derive the header text.
  headerText = productClass?.field_class_product_page_header ?? productClass?.name;

  return (
    <>
      {headerText && (
        <SectionHeading title={headerText} id={productClass.id} link={link}
                        marginBottom={marginBottom}
                        headingClassName={classNames({
                          "max-md:!text-2xl md:!text-4xl" : headerType === 3 && !smallTitle,
                          "text-2xl" : smallTitle,
                        })}
                        className={className} {...props} headerType={headerType ?? 2} />
      )}
      {(!headerText && defaultTitle) && (
        <SectionHeading title={defaultTitle} id={productClass.id} link={link}
                        marginBottom={marginBottom}
                        headingClassName={classNames({
                          "max-md:!text-2xl md:!text-4xl" : headerType === 3 && !smallTitle,
                          "text-2xl" : smallTitle,
                        })}
                        className={className} />
      )}
    </>
  );
}

